/* Copyright (c) 2019-2025, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Injectable, Injector } from '@angular/core';
import { Observable, ReplaySubject, throwError } from 'rxjs';
import { tap, catchError, finalize } from 'rxjs/operators';
import { extend } from 'lodash';
import { AttributeValue, Attributes } from '@opentelemetry/api';
import {
    PropertyBag,
    NVErrorResponse,
    NvRequestOptions
} from 'nv-angular-http-endpoint/lib/nv-http-endpoint/nv-http-endpoint';
import { base64Encode } from 'utils/lib/common/common-utils';
import { Logger, LoggingService } from 'crimson/lib/logging/logging.service';
import { StatusCodeZeroAnalyzerConfig } from 'crimson/lib/analyze-status-code-zero/analyze-status-code-zero';
import { SpanTagNames } from '../constants/distributedtracing_constants';
import {
    HttpPopupErrorDetails,
    OpenTelemetrySpan,
    OpenTelemetryTracer,
    OTELTracerConfiguration,
    SpanTags
} from '../distributed-tracing';
import { DistributedTracingXhrFactoryService } from './distributedtracing-xhrfactory.service';
import { OTELTracerService } from './otel-tracer.service';

@Injectable({
    providedIn: 'root'
})
export class DistributedTracingUtilService {
    private commonTags: SpanTags;
    private tracer: OpenTelemetryTracer;
    private logger: Logger;
    private disableOpenTracingSub: ReplaySubject<boolean>;
    private distributedTracingXhrFactoryService: DistributedTracingXhrFactoryService;

    constructor(
        private openTelemetryService: OTELTracerService,
        private loggingService: LoggingService,
        private injector: Injector
    ) {
        this.logger = this.loggingService.getLogger('DistributedTracingUtilService');
    }

    public setSpanAttribute(span: OpenTelemetrySpan, key: string, value: AttributeValue): void {
        if (!span) {
            this.logger.error(`Unable to set attribute ${key} on null span`);
            return;
        }

        // If the span has already been ended, span.isRecording() will return false.
        // In that case, simply log the error instead of attempting to add the attribute.
        if (!span.isRecording()) {
            this.logger.error(`Unable to set attribute ${key} on closed span`);
            return;
        }

        span.setAttribute(key, value);
    }

    public setSpanAttributes(span: OpenTelemetrySpan, attributes: Attributes): void {
        if (!span) {
            this.logger.error(`Unable to set attributes ${attributes} on null span`);
            return;
        }

        if (!span.isRecording()) {
            this.logger.error(`Unable to set attributes ${attributes} on closed span`);
            return;
        }

        span.setAttributes(attributes);
    }

    public initializeDistributedTracing(
        options: OTELTracerConfiguration,
        statusCodeZeroAnalyzerConfig: StatusCodeZeroAnalyzerConfig,
        disableOpenTracingReplaySubject: ReplaySubject<boolean>,
        globalTagsReplaySubject: ReplaySubject<SpanTags>
    ): void {
        this.disableOpenTracingSub = disableOpenTracingReplaySubject;
        this.tracer = this.openTelemetryService.getOTELTracer(options, disableOpenTracingReplaySubject);

        globalTagsReplaySubject.subscribe(
            (globalTags: SpanTags) => {
                this.commonTags = globalTags;
            },
            err => {
                this.logger.error('Error while setting global tags. No global tags will be used', err);
                this.commonTags = {};
            }
        );

        this.distributedTracingXhrFactoryService = this.injector.get(DistributedTracingXhrFactoryService);
        this.distributedTracingXhrFactoryService.initialize(
            this.tracer,
            options.apiTracingConfig,
            statusCodeZeroAnalyzerConfig,
            globalTagsReplaySubject
        );
    }

    public commitAnyPendingSpans(): Observable<boolean> {
        return this.openTelemetryService.commitAnyPendingSpans();
    }

    public isTracingDisabled(): ReplaySubject<boolean> {
        return this.disableOpenTracingSub;
    }

    public getFormattedUrlForHttpPopupSpan(url: string, span?: OpenTelemetrySpan): string {
        if (span && this.tracer) {
            const tracingParams: PropertyBag = {};
            this.injectSpanHttpHeaders(span, tracingParams);
            const urlObj = new URL(url);
            const tracingParamsStr = JSON.stringify(extend({}, tracingParams));
            const searchParamsStr = urlObj.searchParams.toString();
            url = `${urlObj.origin}${urlObj.pathname}?tracer=${base64Encode(tracingParamsStr, true, true)}`;
            if (searchParamsStr.length) {
                url = `${url}&${searchParamsStr}`;
            }
        }
        return url;
    }

    public startSpan(spanName: string, parentSpan?: OpenTelemetrySpan): OpenTelemetrySpan {
        return this.openTelemetryService.createSpan(spanName, parentSpan);
    }

    public startSpanUsingContext(spanName: string, contextInfo): OpenTelemetrySpan {
        return this.openTelemetryService.createSpanUsingContextInfo(spanName, contextInfo);
    }

    public extractContextFromSpan(span: OpenTelemetrySpan): SpanTags {
        return this.openTelemetryService.getSpanContextInfo(span);
    }

    private injectSpanHttpHeaders(span: OpenTelemetrySpan, headers: PropertyBag) {
        const carrierHeaders = this.openTelemetryService.getSpanContextInfo(span);
        const keys = Object.keys(carrierHeaders);
        for (const key of keys) {
            headers[key] = carrierHeaders[key];
        }
    }

    private handleHttpError(span: OpenTelemetrySpan, error: NVErrorResponse): void {
        this.setSpanAttribute(span, SpanTagNames.Error, true);
        this.setSpanAttribute(span, SpanTagNames.HttpStatusCode, (error && error.status) || 0);
    }

    private handleHttpPopupError(span: OpenTelemetrySpan, errorDetails: HttpPopupErrorDetails) {
        this.setSpanAttribute(span, SpanTagNames.Error, true);
        this.setSpanAttribute(span, SpanTagNames.HttpStatusCode, errorDetails.errorCode);
        this.setSpanAttribute(span, SpanTagNames.HttpErrorText, errorDetails.errorText);
        this.setSpanAttribute(span, SpanTagNames.HttpUrl, errorDetails.failedUrl);
    }

    private handleHttpSuccess(span: OpenTelemetrySpan, url?: string): void {
        this.setSpanAttribute(span, SpanTagNames.Error, false);
        if (url) {
            this.setSpanAttribute(span, SpanTagNames.HttpUrl, url);
        }
    }

    public handleHttpEndpointSpan<T>(
        httpRequest: Observable<T>,
        options: NvRequestOptions,
        spanName: string,
        parentSpan?: OpenTelemetrySpan,
        additionalSpanAttributes?: PropertyBag
    ): Observable<T> {
        const headers: PropertyBag = options.headers ? <PropertyBag>options.headers : {};
        const span = this.startSpan(spanName, parentSpan);
        if (span) {
            const params: PropertyBag = options.params ? <PropertyBag>options.params : {};
            const mergedAttributes: PropertyBag = {
                ...options.params,
                ...additionalSpanAttributes
            };
            this.setSpanAttributes(span, mergedAttributes);
            this.injectSpanHttpHeaders(span, headers);
            return httpRequest.pipe(
                tap(() => this.handleHttpSuccess(span)),
                catchError((error: NVErrorResponse) => {
                    this.handleHttpError(span, error);
                    return throwError(error);
                }),
                finalize(() => this.closeSpan(span))
            );
        } else {
            return httpRequest;
        }
    }

    public closeSpan(span?: OpenTelemetrySpan): void {
        if (span) {
            this.setSpanAttributes(span, this.commonTags);
            span.end();
        }
    }

    public closeHttpPopupSpan(span?: OpenTelemetrySpan, error?: boolean, errorDetails?: HttpPopupErrorDetails): void {
        if (span) {
            this.setSpanAttributes(span, this.commonTags);
            if (error) {
                this.handleHttpPopupError(span, errorDetails);
            } else {
                this.handleHttpSuccess(span, errorDetails.failedUrl);
            }
            span.end();
        }
    }

    public sendOpenTracingSpanTags(
        spanName: string,
        spanTags: Map<string, string>,
        parentSpan?: OpenTelemetrySpan
    ): void {
        const span = this.startSpan(spanName, parentSpan);
        spanTags?.forEach((tagValue, tagName) => {
            this.setSpanAttribute(span, tagName, tagValue);
        });
        this.closeSpan(span);
    }

    /**
     * Execute a function with the active context extracted from spanData
     *
     * @param spanData Carrier data to be extracted and set as the active context during function execution
     * @param fn function to execute in the context
     * @param thisArg optional receiver to be used for calling fn
     * @param args optional arguments forwarded to fn
     */
    // disabled linting to prevent the linter and formatter from fighting with the "F extends (...args: A)"
    // eslint-disable-next-line space-before-function-paren
    public runWithinSpanContext<Carrier, A extends unknown[], F extends (...args: A) => ReturnType<F>>(
        spanData: Carrier,
        fn: F,
        thisArg?: ThisParameterType<F>,
        ...args: A
    ): ReturnType<F> {
        const extractedContext = this.openTelemetryService.extractContext(spanData);
        return this.openTelemetryService.runWithContext(extractedContext, fn, thisArg, ...args);
    }
}
